import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { dirFor } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  apiError,
  MessagingChannel,
  RESERVED_CAMPAIGN_VARIABLES,
  type CreateMessageTemplateInput,
  type MessageTemplate,
  type TemplatePreview,
} from './messagingLib';

const BLANK: CreateMessageTemplateInput = {
  key: '',
  channel: MessagingChannel.EMAIL,
  locale: 'en',
  subject: '',
  body: '',
  variables: [],
};

/**
 * Templates, with locale and preview. `key` groups a template's locale
 * variants — same key and channel, one row per locale — so this list shows
 * every variant, and a new locale for an existing template is a new row
 * with the same key rather than a translation field bolted onto one row.
 *
 * Only EMAIL and SMS are offered as channels: WhatsApp and PUSH are
 * deliberately unimplemented (`messagingLib.ts` header, note 3) and a
 * template that could target them would promise a send nothing can honour.
 */
export default function TemplatesTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('message_templates.manage');

  const [channel, setChannel] = useState<MessagingChannel | ''>('');
  const [rows, setRows] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MessageTemplate | null>(null);
  const [form, setForm] = useState<CreateMessageTemplateInput>(BLANK);
  const [varsText, setVarsText] = useState('');
  const [saving, setSaving] = useState(false);

  const [previewing, setPreviewing] = useState<MessageTemplate | null>(null);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: MessageTemplate[] }>('/messaging/templates', {
        params: { limit: 100, channel: channel || undefined },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('messaging.templates.loadFailed'))))
      .finally(() => setLoading(false));
  }, [channel, t]);

  useEffect(load, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setVarsText('');
    setFormOpen(true);
  };

  const openEdit = (tpl: MessageTemplate) => {
    setEditing(tpl);
    setForm({
      key: tpl.key,
      channel: tpl.channel,
      locale: tpl.locale,
      subject: tpl.subject ?? '',
      body: tpl.body,
      variables: tpl.variables,
      isActive: tpl.isActive,
    });
    setVarsText(tpl.variables.join(', '));
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    const variables = varsText
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    try {
      if (editing) {
        await api.patch(`/messaging/templates/${editing.id}`, {
          subject: form.channel === MessagingChannel.EMAIL ? form.subject || undefined : undefined,
          body: form.body,
          variables,
          isActive: form.isActive,
        });
        toast.success(t('messaging.templates.saved'));
      } else {
        await api.post('/messaging/templates', {
          ...form,
          subject: form.channel === MessagingChannel.EMAIL ? form.subject || undefined : undefined,
          variables,
        });
        toast.success(t('messaging.templates.created'));
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('messaging.templates.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tpl: MessageTemplate) => {
    try {
      await api.delete(`/messaging/templates/${tpl.id}`);
      toast.success(t('messaging.templates.removed'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('messaging.templates.removeFailed')));
    }
  };

  const openPreview = (tpl: MessageTemplate) => {
    setPreviewing(tpl);
    setPreview(null);
    setPreviewVars(Object.fromEntries(tpl.variables.map((v) => [v, ''])));
  };

  const runPreview = async () => {
    if (!previewing) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.post<TemplatePreview>(`/messaging/templates/${previewing.id}/preview`, {
        variables: previewVars,
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e, t('messaging.templates.previewFailed')));
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          <Field label={t('messaging.templates.channel')}>
            <Select value={channel} onChange={(e) => setChannel(e.target.value as MessagingChannel | '')}>
              <option value="">{t('messaging.templates.anyChannel')}</option>
              {Object.values(MessagingChannel).map((c) => (
                <option key={c} value={c}>
                  {t(`crm.consent.channel.${c}`)}
                </option>
              ))}
            </Select>
          </Field>
          {canManage && (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('messaging.templates.newTemplate')}
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('messaging.templates.empty')}
          hint={t('messaging.templates.emptyHint')}
          action={
            canManage ? (
              <Button size="sm" onClick={openCreate}>
                {t('messaging.templates.newTemplate')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('messaging.templates.key')}</TH>
                <TH>{t('messaging.templates.channel')}</TH>
                <TH>{t('messaging.templates.locale')}</TH>
                <TH>{t('common.status')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((tpl) => (
                <TR key={tpl.id}>
                  <TD className="font-medium">{tpl.key}</TD>
                  <TD>{t(`crm.consent.channel.${tpl.channel}`)}</TD>
                  <TD className="tabular-nums">{tpl.locale}</TD>
                  <TD>
                    <Badge tone={tpl.isActive ? 'success' : 'neutral'}>
                      {t(tpl.isActive ? 'messaging.templates.active' : 'messaging.templates.inactive')}
                    </Badge>
                  </TD>
                  <TD className="text-end">
                    <div className="flex justify-end gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => openPreview(tpl)}>
                        {t('messaging.templates.preview')}
                      </Button>
                      {canManage && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(tpl)}>
                            {t('common.edit')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => void remove(tpl)}>
                            {t('common.delete')}
                          </Button>
                        </>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'messaging.templates.editTitle' : 'messaging.templates.newTemplate')}
        className="max-w-lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving || !form.key || !form.body} onClick={() => void submit()}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {!editing && (
            <>
              <Field label={t('messaging.templates.key')} hint={t('messaging.templates.keyHint')}>
                <Input value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('messaging.templates.channel')}>
                  <Select
                    value={form.channel}
                    onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value as MessagingChannel }))}
                  >
                    {Object.values(MessagingChannel).map((c) => (
                      <option key={c} value={c}>
                        {t(`crm.consent.channel.${c}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('messaging.templates.locale')}>
                  <Input value={form.locale} onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))} />
                </Field>
              </div>
            </>
          )}
          {form.channel === MessagingChannel.EMAIL && (
            <Field label={t('messaging.templates.subject')}>
              {/* `dir`/`lang` follow the TEMPLATE's own locale, not the
                  dashboard's current UI language — an Arabic-UI operator
                  writing an English confirmation SMS must still type it
                  left-to-right, and the reverse. See `crm.templates` in the
                  original brief for this screen: a template editor is a
                  special case, and this was the one place the recovered
                  console still inherited the page direction. */}
              <Input
                dir={dirFor(form.locale ?? 'en')}
                lang={form.locale}
                value={form.subject ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              />
            </Field>
          )}
          <Field label={t('messaging.templates.body')} hint={t('messaging.templates.bodyHint')}>
            <Textarea
              dir={dirFor(form.locale ?? 'en')}
              lang={form.locale}
              rows={5}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
          </Field>
          <Field
            label={t('messaging.templates.variables')}
            hint={t('messaging.templates.variablesHint', { list: RESERVED_CAMPAIGN_VARIABLES.join(', ') })}
          >
            <Input value={varsText} onChange={(e) => setVarsText(e.target.value)} placeholder="customerName, unsubscribeUrl" />
          </Field>
          {editing && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive ?? true}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="h-4 w-4"
              />
              {t('messaging.templates.active')}
            </label>
          )}
        </div>
      </Dialog>

      <Dialog
        open={Boolean(previewing)}
        onClose={() => setPreviewing(null)}
        title={t('messaging.templates.previewTitle')}
        className="max-w-lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPreviewing(null)}>
              {t('common.close')}
            </Button>
            <Button disabled={previewLoading} onClick={() => void runPreview()}>
              {t('messaging.templates.preview')}
            </Button>
          </div>
        }
      >
        {previewing && (
          <div className="space-y-3">
            {previewing.variables.map((v) => (
              <Field key={v} label={v}>
                <Input
                  value={previewVars[v] ?? ''}
                  onChange={(e) => setPreviewVars((p) => ({ ...p, [v]: e.target.value }))}
                />
              </Field>
            ))}
            {preview && (
              <div className="space-y-1 rounded-lg border bg-subtle p-3 text-sm">
                {preview.subject && (
                  <p className="font-medium" dir={dirFor(previewing?.locale ?? 'en')} lang={previewing?.locale}>
                    {preview.subject}
                  </p>
                )}
                <p
                  className="whitespace-pre-wrap"
                  dir={dirFor(previewing?.locale ?? 'en')}
                  lang={previewing?.locale}
                >
                  {preview.body}
                </p>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
